import {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';

import { useSelector } from '@/api/state';
import { Button } from '@payaca/ambrosia';
import BasicField from '@payaca/components/basicField/BasicField';
import CheckboxField from '@payaca/components/checkboxField/CheckboxField';
import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';
import TextareaFieldFormatter from '@payaca/components/textareaField/TextareaFieldFormatter';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import ValidationFeedbackBlock from '@payaca/components/validationFeedbackBlock/ValidationFeedbackBlock';
import { VALID_EMAIL_REGEX } from '@payaca/constants';
import {
  getIsRequiredFieldValidator,
  getRegexMatchFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import { formatPhoneNumber } from '@payaca/helpers/phoneNumberHelper';
import { requestSendPurchaseOrder } from '@payaca/store/materialsList/materialsListActions';
import { SendPurchaseOrderRequestData } from '@payaca/store/materialsList/materialsListTypes';
import { requestPersistSupplier } from '@payaca/store/suppliers/suppliersActions';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { HydratedPurchaseOrder } from '@payaca/types/materialsListTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useDispatch } from 'react-redux';
import LoqateAdvisoryEmailInputField from '../loqateAdvisoryEmailInputField/LoqateAdvisoryEmailInputField';
import './SendPurchaseOrderControl.sass';

const supplierEmailAddressFieldValidator =
  getRegexMatchFieldValidator(VALID_EMAIL_REGEX);

type Props = {
  purchaseOrder: HydratedPurchaseOrder;
  onSendPurchaseOrderSuccess?: () => void;
};
const SendPurchaseOrderControl: FC<Props> = ({
  purchaseOrder,
  onSendPurchaseOrderSuccess,
}: PropsWithChildren<Props>): JSX.Element => {
  const dispatch = useDispatch();
  const supplier = purchaseOrder.supplier;
  const [showValidationFeedback, setShowValidationFeedback] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMarkAsSentProcessing, setIsMarkAsSentProcessing] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState(false);

  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const myProfile = useSelector((state: any) => state.users.myProfile);

  const defaultEmailBody = useMemo(() => {
    return `Hi ${supplier?.name}\n\nPlease find attached **purchase order ${purchaseOrder?.reference}** for **${
      account.companyName
    }**\n\n${
      account.email || account.contactNumber
        ? `Please confirm our order using these details:\n${
            account.email
              ? `- [${account.email}](mailto:${account.email})\n`
              : ''
          }${
            account.contactNumber
              ? `- [${formatPhoneNumber(account.contactNumber)}](tel:${
                  account.contactNumber
                })\n\n\n`
              : ''
          }`
        : ``
    }Kind regards,\n\n${myProfile.firstname} ${myProfile.lastname}\n\n${
      account.companyName
    }`;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'purchaseOrder.reference'
  }, [myProfile, account, supplier]);

  const fieldValidators = useMemo(() => {
    return {
      emailSubject: [
        getIsRequiredFieldValidator({
          customErrorMessage: 'Email subject is required',
        }),
      ],
      emailBody: [
        getIsRequiredFieldValidator({
          customErrorMessage: 'Email body is required',
        }),
      ],
      supplierEmailAddress: [
        getIsRequiredFieldValidator({
          customErrorMessage:
            'Please add a supplier email address or mark as sent offline',
        }),
        supplierEmailAddressFieldValidator,
      ],
    };
  }, []);

  const initialFormState = useMemo(() => {
    return {
      id: purchaseOrder.id,
      emailSubject: `Purchase order ${purchaseOrder?.reference} from ${account.companyName}`,
      emailBody: defaultEmailBody,
      copyMeIn: true,
      supplierEmailAddress: supplier?.emailAddress,
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'supplier.emailAddress'
  }, [purchaseOrder, account, defaultEmailBody]);

  const onSubmit = useCallback(
    (formState: SendPurchaseOrderRequestData) => {
      setIsProcessing(true);
      dispatch(
        requestSendPurchaseOrder(
          formState,
          () => {
            setIsProcessing(false);
            onSendPurchaseOrderSuccess?.();
          },
          () => {
            setIsProcessing(false);
            setSubmissionError(
              'Something went wrong sending this purchase order.'
            );
          }
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [onSendPurchaseOrderSuccess]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <>
          <div className="form-body">
            <div>
              Sending to <strong>{supplier?.name} </strong>
              {!overrideEmail && (
                <>
                  {supplier?.emailAddress && (
                    <>
                      ({supplier?.emailAddress})
                      <Button
                        variant="link"
                        onClick={() => setOverrideEmail(true)}
                      >
                        Override email address for this PO
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
            {(overrideEmail || !supplier?.emailAddress) && (
              <ValidatedFieldWrapper
                isTouched={touchedState?.supplierEmailAddress ?? false}
                validationResult={{
                  isValid:
                    validationState.supplierEmailAddress?.isValid ?? false,
                }}
              >
                <LoqateAdvisoryEmailInputField
                  label={
                    overrideEmail ? 'Email address' : 'Supplier email address'
                  }
                  name="supplierEmailAddress"
                  value={formState.supplierEmailAddress}
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                />
              </ValidatedFieldWrapper>
            )}
            <ValidatedFieldWrapper
              isTouched={touchedState?.emailSubject ?? false}
              validationResult={{
                isValid: validationState.emailSubject?.isValid ?? false,
              }}
            >
              <BasicField
                label="Email subject"
                name="emailSubject"
                value={formState.emailSubject}
                onChange={onFieldChange}
                onTouch={onFieldTouch}
              />
            </ValidatedFieldWrapper>
            <ValidatedFieldWrapper
              isTouched={touchedState?.emailBody ?? false}
              validationResult={{
                isValid: validationState.emailBody?.isValid ?? false,
              }}
            >
              <TextareaFieldFormatter
                label="Email body"
                name={'emailBody'}
                value={formState.emailBody}
                onChange={onFieldChange}
                onTouch={onFieldTouch}
              />
            </ValidatedFieldWrapper>
            {purchaseOrder?.pdfUrl && (
              <a
                href={purchaseOrder.pdfUrl}
                className="download-pdf-link"
                target="_blank"
                rel="noopener noreferrer"
                download={`purchase-order-${purchaseOrder.reference}.pdf`}
              >
                <UntitledIcon name="paperclip.3" />
                <span>purchase-order-{purchaseOrder.reference}.pdf</span>
              </a>
            )}

            <CheckboxField
              name="copyMeIn"
              value={formState.copyMeIn}
              label={'Copy me in to this email'}
              onChange={onFieldChange}
            />
          </div>
          <div className="actions-container">
            {submissionError && (
              <FeedbackBlock
                isDismissable={true}
                onDismissed={() => setSubmissionError('')}
                feedbackLevel={FeedbackLevel.ERROR}
              >
                <span>{submissionError}</span>
              </FeedbackBlock>
            )}
            {showValidationFeedback && (
              <ValidationFeedbackBlock
                validationResults={Object.values(validationState)}
                isDismissable={true}
                onDismissed={() => setShowValidationFeedback(false)}
              />
            )}
            <Button
              isProcessing={isProcessing}
              replaceContentWhenProcessing
              onClick={() => {
                if (isProcessing) return;
                if (isValid) {
                  if (!supplier?.emailAddress && !supplier.isSystemManaged) {
                    setIsProcessing(true);
                    dispatch(
                      requestPersistSupplier(
                        {
                          id: supplier?.id,
                          emailAddress: formState.supplierEmailAddress,
                        },
                        () => {
                          onSubmit(formState as SendPurchaseOrderRequestData);
                        }
                      )
                    );
                  } else {
                    onSubmit(formState as SendPurchaseOrderRequestData);
                  }
                } else {
                  onFieldTouch('supplierEmailAddress');
                  setShowValidationFeedback(true);
                }
              }}
            >
              Send
            </Button>
          </div>
        </>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'purchaseOrder.reference', 'dispatch', and 'purchaseOrder.pdfUrl'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: isMarkAsSentProcessing
    [
      supplier,
      showValidationFeedback,
      onSubmit,
      isProcessing,
      submissionError,
      isMarkAsSentProcessing,
      overrideEmail,
    ]
  );

  return (
    <div className="send-purchase-order-control">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
        fieldValidators={fieldValidators}
      />
    </div>
  );
};

export default SendPurchaseOrderControl;
